import React, { useEffect, useState } from 'react';
import { api } from '../api/client';

interface Profile {
  name: string;
  email: string;
}

interface Workspace {
  name: string;
}

export default function Settings() {
  const [profile, setProfile] = useState<Profile>({ name: '', email: '' });
  const [workspace, setWorkspace] = useState<Workspace>({ name: '' });
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingWorkspace, setSavingWorkspace] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  async function loadSettings() {
    try {
      setLoading(true);
      setError('');
      const [pRes, wRes] = await Promise.all([
        api.get('/api/settings/profile'),
        api.get('/api/settings/workspace'),
      ]);
      setProfile(pRes.data || pRes);
      setWorkspace(wRes.data || wRes);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleSaveProfile(e: React.FormEvent) {
    e.preventDefault();
    try {
      setSavingProfile(true);
      setError('');
      setSuccess('');
      await api.put('/api/settings/profile', profile);
      setSuccess('Profile saved');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSavingProfile(false);
    }
  }

  async function handleSaveWorkspace(e: React.FormEvent) {
    e.preventDefault();
    try {
      setSavingWorkspace(true);
      setError('');
      setSuccess('');
      await api.put('/api/settings/workspace', workspace);
      setSuccess('Workspace saved');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSavingWorkspace(false);
    }
  }

  return (
    <div>
      <h1 style={styles.heading}>Settings</h1>
      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <h2 style={styles.subheading}>Profile</h2>
          <form onSubmit={handleSaveProfile} style={styles.card}>
            <div style={styles.field}>
              <label style={styles.label}>Name</label>
              <input
                value={profile.name}
                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                style={styles.input}
                required
              />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Email</label>
              <input
                type="email"
                value={profile.email}
                onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                style={styles.input}
                required
              />
            </div>
            <button type="submit" disabled={savingProfile} style={styles.button}>
              {savingProfile ? 'Saving...' : 'Save Profile'}
            </button>
          </form>

          <h2 style={{ ...styles.subheading, marginTop: 24 }}>Workspace</h2>
          <form onSubmit={handleSaveWorkspace} style={styles.card}>
            <div style={styles.field}>
              <label style={styles.label}>Workspace Name</label>
              <input
                value={workspace.name}
                onChange={(e) => setWorkspace({ name: e.target.value })}
                style={styles.input}
                required
              />
            </div>
            <button type="submit" disabled={savingWorkspace} style={styles.button}>
              {savingWorkspace ? 'Saving...' : 'Save Workspace'}
            </button>
          </form>
        </>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  heading: { fontSize: 20, fontWeight: 600, marginBottom: 16 },
  error: { background: '#fef2f2', color: '#991b1b', padding: 10, borderRadius: 8, marginBottom: 12 },
  success: { background: '#f0fdf4', color: '#166534', padding: 10, borderRadius: 8, marginBottom: 12 },
  subheading: { fontSize: 16, fontWeight: 600, marginBottom: 12 },
  card: { background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', padding: 20, maxWidth: 480 },
  field: { marginBottom: 16 },
  label: { display: 'block', fontSize: 14, color: '#374151', marginBottom: 4, fontWeight: 500 },
  input: { padding: '8px 10px', fontSize: 14, border: '1px solid #e5e7eb', borderRadius: 6, width: '100%', boxSizing: 'border-box' },
  button: { padding: '8px 16px', fontSize: 14, background: '#111827', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' },
};
